import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Chart } from "@astrodraw/astrochart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#A8874F";
const SIGNS = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];
const sym = (s) => `/starhaven/${String(s).toLowerCase()}.png`;

const T = {
  ivory: "#F8F5F1", cream: "#F2EAE2", altar: "#FBF8F3", linen: "#EFE4D8",
  brown: "#3F332E", espresso: "#4B3A32", taupe: "#6A5A50",
  terra: "#BF826A", terraH: "#A96F5D", gold: GOLD, border: "#E3D8CC",
};
const serif = { fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif" };
const sans = { fontFamily: "'Inter', system-ui, sans-serif" };

function ZodiacCircle({ size }) {
  const r = size / 2, inner = r - 34;
  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto" }} data-testid="zodiac-circle">
      <div style={{ position: "absolute", inset: "10%", borderRadius: "50%",
        background: "radial-gradient(circle at 50% 45%, rgba(191,130,106,0.10), rgba(248,245,241,0) 70%)" }} />
      <svg width={size} height={size} style={{ position: "absolute", inset: 0 }}>
        <circle cx={r} cy={r} r={inner} fill="none" stroke={GOLD} strokeWidth="1" opacity="0.55" />
        <circle cx={r} cy={r} r={inner - 12} fill="none" stroke={GOLD} strokeWidth="0.6" opacity="0.35" />
      </svg>
      {SIGNS.map((s, i) => {
        const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
        const x = r + (inner - 6) * Math.cos(a), y = r + (inner - 6) * Math.sin(a);
        return <img key={s} src={sym(s)} alt={s} title={s}
          style={{ position: "absolute", width: size * 0.072, height: size * 0.072,
            left: x, top: y, transform: "translate(-50%,-50%)", objectFit: "contain" }} />;
      })}
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 8%" }}>
        <div className="sh-entry-title">CRYSTAL CHART</div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      <span style={{ ...sans, display: "block", fontSize: 11, letterSpacing: 1.5,
        textTransform: "uppercase", color: T.taupe, marginBottom: 6 }}>{label}</span>
      {children}
    </label>
  );
}
const inputStyle = { ...sans, width: "100%", boxSizing: "border-box", padding: "12px 14px",
  background: T.altar, border: `1px solid ${T.border}`, borderRadius: 10, color: T.espresso,
  fontSize: 15, outline: "none" };
const maskBox = { display: "flex", alignItems: "center", gap: 4, background: "#FBF8F3",
  border: "1px solid #E3D8CC", borderRadius: 10, padding: "12px 14px" };
const maskSep = { fontFamily: "'Inter', sans-serif", color: "#6A5A50", fontSize: 16 };
const maskInp = (w) => ({ fontFamily: "'Inter', sans-serif", border: "none", outline: "none",
  background: "transparent", color: "#3F332E", fontSize: 16, textAlign: "center",
  width: w === 4 ? 52 : 30, padding: 0 });

function PrimaryButton({ children, onClick, disabled, testid }) {
  const [h, setH] = useState(false);
  return <button data-testid={testid} onClick={onClick} disabled={disabled}
    onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
    style={{ ...sans, width: "100%", padding: "13px 20px", border: "none", borderRadius: 999,
      background: disabled ? "#cbb9ab" : (h ? T.terraH : T.terra), color: T.altar, fontSize: 15,
      letterSpacing: 0.5, cursor: disabled ? "default" : "pointer", transition: "background-color .2s" }}>
    {children}</button>;
}

const PLANET_MAP = { sun: "Sun", moon: "Moon", mercury: "Mercury", venus: "Venus",
  mars: "Mars", jupiter: "Jupiter", saturn: "Saturn", uranus: "Uranus",
  neptune: "Neptune", pluto: "Pluto", chiron: "Chiron", north_node: "NNode", south_node: "SNode" };

const GLYPH = { sun: "☉", moon: "☽", mercury: "☿", venus: "♀", mars: "♂",
  jupiter: "♃", saturn: "♄", uranus: "♅", neptune: "♆", pluto: "♇",
  north_node: "☊", south_node: "☋", chiron: "⚷" };
const LEGEND_ORDER = ["sun","moon","mercury","venus","mars","jupiter","saturn",
  "uranus","neptune","pluto","north_node","south_node","chiron"];
const NAME = { sun:"Sun", moon:"Moon", mercury:"Mercury", venus:"Venus", mars:"Mars",
  jupiter:"Jupiter", saturn:"Saturn", uranus:"Uranus", neptune:"Neptune", pluto:"Pluto",
  north_node:"North Node", south_node:"South Node", chiron:"Chiron" };
const ordinal = (n) => { const s = ["th","st","nd","rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]); };

function Legend({ wheel }) {
  const byName = {};
  (wheel.points || []).forEach((p) => { byName[p.name.toLowerCase()] = p; });
  const rows = LEGEND_ORDER.filter((k) => byName[k] && GLYPH[k]).map((k) => byName[k]);
  return (
    <div style={{ flex: "1 1 250px", minWidth: 240 }} data-testid="wheel-legend">
      <h3 className="sh-subhead">Your Chart Placements</h3>
      <div>
        {rows.map((p) => {
          const k = p.name.toLowerCase();
          return (
            <div key={k} data-testid={`legend-${k}`} style={{ display: "flex", alignItems: "baseline",
              gap: 12, padding: "5px 0" }}>
              <span style={{ color: "#A8874F", fontSize: 20, width: 24, textAlign: "center", flexShrink: 0 }}>{GLYPH[k]}</span>
              <span className="sh-legend-text">{NAME[k]} · {p.sign}{p.house ? ` · ${ordinal(p.house)} house` : ""}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Wheel({ wheel }) {
  const ref = useRef(null);
  const sunRef = useRef(null);
  const moonRef = useRef(null);
  const [w, setW] = useState(480);
  useEffect(() => {
    const fit = () => setW(Math.min(500, Math.max(300, Math.min(500, window.innerWidth - 60))));
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, []);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.innerHTML = "";
    const planetsData = {};
    (wheel.points || []).forEach((p) => {
      const key = PLANET_MAP[p.name.toLowerCase()];
      if (key) planetsData[key] = [p.longitude];
    });
    // Whole Sign houses: 12 cusps at sign boundaries starting from the Ascendant's sign.
    const ascSign = wheel.ascendant?.sign_index ?? 0;
    const cusps = Array.from({ length: 12 }, (_, i) => ((ascSign * 30) + i * 30) % 360);
    const settings = {
      COLOR_BACKGROUND: "#FBF8F3",           // inner house-area ground
      COLORS_SIGNS: Array(12).fill("#F2EAE2"), // outer sign-band ground (uniform, no rainbow)
      POINTS_COLOR: "#3F332E",               // strongest: planetary glyphs
      SIGNS_COLOR: "#A8874F",                // middle: zodiac sign glyphs
      LINE_COLOR: "#D7D2CA",                 // faintest: division lines / ticks
      CIRCLE_COLOR: "#D7D2CA",               // ring boundary lines
      CUSPS_FONT_COLOR: "#D7D2CA",           // faintest: house numbers
      SYMBOL_AXIS_FONT_COLOR: "#6A5A50",     // As / Ds / Mc / Ic labels
      CIRCLE_STRONG: 1, CUSPS_STROKE: 1,     // 1px boundary + house lines
      SYMBOL_SCALE: 1, ADD_CLICK_AREA: false, SHOW_DIGNITIES_TEXT: false,
    };
    try {
      const chart = new Chart(el.id, w, w, settings);
      chart.radix({ planets: planetsData, cusps });
    } catch (e) { /* render guard */ }
    // ---- presentation-only post-processing (no calculation/position/library change) ----
    try {
      const svg = el.querySelector("svg");
      if (svg) {
        // (1) house lines complete + unbroken, glyphs above lines: move planets group on top
        const pgroup = svg.querySelector('[id$="-planets"]');
        if (pgroup) pgroup.parentNode.appendChild(pgroup);
        svg.querySelectorAll("line").forEach((ln) => {
          ln.setAttribute("stroke", "#D7D2CA"); ln.setAttribute("stroke-width", "1");
        });
        // (2) planetary glyphs ~1.4x (zodiac signs left unchanged)
        if (pgroup) pgroup.querySelectorAll(":scope > *").forEach((node) => {
          try {
            const bb = node.getBBox();
            const gx = bb.x + bb.width / 2, gy = bb.y + bb.height / 2;
            const prev = node.getAttribute("transform") || "";
            node.setAttribute("transform", `${prev} translate(${gx} ${gy}) scale(1.4) translate(${-gx} ${-gy})`);
          } catch (e) { /* skip node */ }
        });
        // (3) centre Sun/Moon glyphs: clone the chart library's own planetary glyphs
        // (never Unicode/emoji) into the centre, tinted antique gold.
        const injectGlyph = (holder, suffix) => {
          if (!holder) return;
          holder.innerHTML = "";
          const src = svg.querySelector(`[id$="-planets-${suffix}"]`);
          if (!src) return;
          const clone = src.cloneNode(true);
          clone.removeAttribute("transform"); clone.removeAttribute("id");
          [clone, ...clone.querySelectorAll("*")].forEach((n) => {
            if (n.getAttribute && n.getAttribute("stroke")) n.setAttribute("stroke", "#A8874F");
            const f = n.getAttribute && n.getAttribute("fill");
            if (f && f !== "none" && f !== "transparent") n.setAttribute("fill", "#A8874F");
          });
          holder.appendChild(clone);
          try {
            const bb = clone.getBBox();
            const pad = Math.max(bb.width, bb.height) * 0.18;
            holder.setAttribute("viewBox", `${bb.x - pad} ${bb.y - pad} ${bb.width + 2 * pad} ${bb.height + 2 * pad}`);
          } catch (e) { /* bbox guard */ }
        };
        injectGlyph(sunRef.current, "Sun");
        injectGlyph(moonRef.current, "Moon");
      }
    } catch (e) { /* post-process guard */ }
  }, [wheel, w]);
  // Centre: Sun / Moon / Rising from this chart's own data. Sun & Moon use the chart
  // library's planetary glyphs; Rising uses the supplied gold zodiac asset. No Unicode/emoji.
  const findSign = (nm) => { const p = (wheel.points || []).find((x) => x.name.toLowerCase() === nm); return p ? p.sign : ""; };
  const ascIdx = wheel.ascendant?.sign_index ?? 0;
  const ascName = wheel.ascendant?.sign || SIGNS[ascIdx] || "";
  const fsz = w < 380 ? 15 : 17;
  const gsz = fsz + 4;
  const centreRows = [
    { label: "Sun", glyphRef: sunRef, sign: findSign("sun") },
    { label: "Moon", glyphRef: moonRef, sign: findSign("moon") },
    { label: "Rising", asset: sym(ascName), sign: ascName },
  ];
  return (
    <div style={{ position: "relative", width: w, maxWidth: 500, margin: "0 auto" }}>
      <div id="sh-astrochart" ref={ref} data-testid="natal-wheel" style={{ width: w }} />
      <div data-testid="wheel-center" style={{ position: "absolute", inset: 0, display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
        {centreRows.map((r) => (
          <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "center",
            gap: 6, height: fsz * 1.8 }}>
            <span style={{ ...sans, fontSize: fsz, color: "#3F332E" }}>{r.label}</span>
            {r.asset
              ? <img src={r.asset} alt={r.sign} data-testid="rising-zodiac-asset"
                  style={{ height: gsz, width: "auto", display: "block" }} />
              : <svg ref={r.glyphRef} data-testid={`centre-glyph-${r.label.toLowerCase()}`}
                  width={gsz} height={gsz} style={{ display: "block", overflow: "visible" }} />}
            <span style={{ ...sans, fontSize: fsz, color: "#3F332E" }}>{r.sign}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SectionTitle = () => null;

function StonePanel({ role, part }) {
  const st = part?.stone;
  return (
    <div className="sh-sub" style={{ padding: "22px 0 0", borderTop: `1px solid ${T.border}` }}
      data-testid={`stone-${role.toLowerCase()}`}>
      {st?.image && (
        <img src={st.image} alt={`${st.name} crystal`} loading="lazy" data-testid="stone-image"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{ width: "100%", maxWidth: 420, height: "auto", objectFit: "contain",
            display: "block", margin: "0 auto 20px", borderRadius: 14,
            border: `1px solid ${T.border}`, background: T.altar }} />
      )}
      <div>
        <div className="sh-label">{role}</div>
        <h3 className="sh-subhead">{st ? st.name : "—"}</h3>
        {part?.dimension_label && <p className="sh-meta" style={{ marginBottom: 18 }}>Raised by your chart's need for {part.dimension_label.toLowerCase()}.</p>}
        {part?.integration_interpretation && <p className="sh-body">{part.integration_interpretation}</p>}
        {st?.description && <p className="sh-body">{st.description}</p>}
        {st?.practice_guidance && <p className="sh-body">{st.practice_guidance}</p>}
      </div>
    </div>
  );
}

function Reading({ reading }) {
  const rd = reading;
  const o = rd.opening;
  return (
    <div className="sh-reading" style={{ maxWidth: 760, margin: "0 auto", padding: "48px 22px 90px" }} data-testid="reading">
      <div className="sh-section">
        <SectionTitle n={1} />
        <h2 className="sh-section-title">Your Crystal Chart</h2>
        <div style={{ marginBottom: 6 }}>
          <div className="sh-subhead" style={{ marginBottom: 6 }}>{o.name}</div>
          <div className="sh-meta">{o.birth_date}{o.birth_place && ` · ${o.birth_place}`}</div>
        </div>
        <div style={{ margin: "24px 0 10px", display: "flex", flexWrap: "wrap", gap: 32,
          alignItems: "flex-start", justifyContent: "center" }}>
          <div style={{ flex: "0 1 auto" }}><Wheel wheel={rd.wheel} /></div>
          <Legend wheel={rd.wheel} />
        </div>
      </div>

      <div className="sh-section">
        <SectionTitle n={2} />
        <h2 className="sh-section-title">Your Natal Sky</h2>
        {["sun", "moon", "ascendant"].map((k, idx) => {
          const p = rd.natal_sky[k]; if (!p) return null;
          return <div key={k} className={idx > 0 ? "sh-sub" : ""} style={{ padding: "16px 0 0", borderTop: `1px solid ${T.border}`, display: "flex", gap: 18 }} data-testid={`placement-${k}`}>
            <img src={p.symbol} alt={p.sign} style={{ width: 44, height: 44, objectFit: "contain", flexShrink: 0 }} />
            <div>
              <div className="sh-label">{k === "ascendant" ? "Ascendant" : k}</div>
              <div className="sh-subhead">{p.sign}</div>
              <p className="sh-body">{p.text}</p>
            </div>
          </div>;
        })}
      </div>

      {rd.chart_shape?.length > 0 && <div className="sh-section">
        <SectionTitle n={3} />
        <h2 className="sh-section-title">The Shape of Your Chart</h2>
        {rd.chart_shape.map((s, i) => <div key={i} className={i > 0 ? "sh-sub" : ""} data-testid="shape-item">
          <div className="sh-subhead">{s.label}</div>
          <p className="sh-body">{s.text}</p>
        </div>)}
      </div>}

      {rd.diagnosis && <div className="sh-section">
        <SectionTitle n={4} />
        <h2 className="sh-section-title">What Your Chart Is Showing</h2>
        <p className="sh-body">{rd.diagnosis.text}</p>
      </div>}

      <div className="sh-section">
        <SectionTitle n={5} />
        <h2 className="sh-section-title">Your Crystal Architecture</h2>
        <p className="sh-body">{rd.crystal_architecture.text}</p>
        {rd.crystal_architecture.anchor && <StonePanel role="Anchor" part={rd.crystal_architecture.anchor} />}
        {rd.crystal_architecture.support?.length > 0 && (
          <div style={{ marginTop: 36 }} data-testid="support-stones">
            <p className="sh-body" style={{ marginBottom: 4 }}>{rd.crystal_architecture.support_intro}</p>
            {rd.crystal_architecture.support.map((blk) => (
              <StonePanel key={blk.role} role={blk.role} part={blk} />
            ))}
          </div>
        )}
        {rd.crystal_architecture.threshold?.qualified && rd.crystal_architecture.threshold.stone
          ? <StonePanel role="Threshold" part={rd.crystal_architecture.threshold} />
          : <div className="sh-sub" style={{ padding: "22px 0 0", borderTop: `1px solid ${T.border}` }} data-testid="no-threshold">
              <div className="sh-label">Threshold</div>
              <p className="sh-body">{rd.crystal_architecture.threshold?.no_threshold_text}</p>
            </div>}
      </div>

      <div className="sh-section">
        <SectionTitle n={6} />
        <h2 className="sh-section-title">Working With Your Stones</h2>
        <p className="sh-body">{rd.working_with_stones.general}</p>
        {rd.working_with_stones.stones.map((s) => <div key={s.id} className="sh-sub" style={{ padding: "14px 0 0", borderTop: `1px solid ${T.border}` }}>
          <div className="sh-subhead">{s.name}</div>
          <p className="sh-body">{s.practice_guidance}</p>
        </div>)}
      </div>
    </div>
  );
}

export default function StarHaven() {
  const [step, setStep] = useState("entry");
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null);
  const [birthConfirm, setBirthConfirm] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", birth_time_accuracy: "exact", location: "" });
  const [dt, setDt] = useState({ day: "", month: "", year: "", hour: "", minute: "" });
  const seg = { day: useRef(), month: useRef(), year: useRef(), hour: useRef(), minute: useRef() };
  const onSeg = (k, max, nextRef) => (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, max);
    setDt((s) => ({ ...s, [k]: v }));
    if (v.length === max && nextRef?.current) nextRef.current.focus();
  };
  const onSegKey = (k, prevRef) => (e) => {
    if (e.key === "Backspace" && !dt[k] && prevRef?.current) prevRef.current.focus();
  };
  const [size, setSize] = useState(460);
  useEffect(() => {
    const fit = () => setSize(Math.min(460, Math.max(270, window.innerWidth - 60)));
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, []);

  const enter = async () => {
    setErr(null); setLoading(true);
    try {
      const r = await axios.post(`${API}/starhaven/access/validate`, { code });
      if (r.data.redeemed && r.data.chart_id) {
        const rd = await axios.get(`${API}/starhaven/reading/${r.data.chart_id}`);
        setReading(rd.data.reading); setStep("reading");
      } else setStep("birth");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not validate code."); }
    finally { setLoading(false); }
  };
  const reviewBirth = () => {
    setErr(null);
    const { day, month, year, hour, minute } = dt;
    const d = +day, mo = +month, y = +year, h = +hour, mi = +minute;
    if (!(y >= 1 && mo >= 1 && mo <= 12 && d >= 1)) return setErr("Please enter a valid birth date.");
    const dim = new Date(y, mo, 0).getDate();
    if (d > dim) return setErr("Please enter a valid birth date.");
    if (!(h >= 0 && h <= 23 && mi >= 0 && mi <= 59) || !hour || !minute) return setErr("Please enter a valid birth time.");
    setBirthConfirm(true);
  };
  const createChart = async () => {
    setErr(null);
    const { day, month, year, hour, minute } = dt;
    const birth_date = `${String(+year).padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    const birth_time = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/starhaven/access/redeem`,
        { ...form, birth_date, birth_time, code });
      setReading(r.data.reading); setStep("reading");
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409 || st === 422 || st === 400)
        setErr("We couldn't find that birthplace. Please check the city and country and try again.");
      else setErr("Something went wrong opening your chart. Please try again.");
    } finally { setLoading(false); }
  };
  const resendAccess = async () => {
    setResendMsg(""); setResendLoading(true);
    try {
      await axios.post(`${API}/starhaven/access/resend`, { email: resendEmail });
      setResendMsg("If that email matches an order, we've sent your access code to it.");
    } catch (e) {
      setResendMsg("If that email matches an order, we've sent your access code to it.");
    } finally { setResendLoading(false); }
  };
  const ACCURACY_LABEL = { exact: "Exact", approximate: "Approximate", unknown: "Unknown" };

  return (
    <div style={{ minHeight: "100vh", background: T.ivory, ...sans }}>
      {step === "entry" && (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "flex-start", padding: "42px 20px 60px",
          background: `radial-gradient(1200px 700px at 50% 30%, ${T.cream}, ${T.ivory})` }}>
          <img src="/starhaven/amarushaya-logo.webp" alt="Amarushaya" data-testid="logo"
            style={{ width: "min(150px, 42vw)", minWidth: 100, height: "auto", marginBottom: 30 }} />
          <ZodiacCircle size={size} />
          <div style={{ maxWidth: 360, width: "100%", marginTop: 36 }}>
            <p style={{ ...sans, textAlign: "center", color: T.taupe, fontSize: 14.5, lineHeight: 1.7, marginBottom: 18 }}>
              Enter your private access code to open your Crystal Chart reading.</p>
            <input data-testid="access-code-input" value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="Access code" style={{ ...inputStyle, textAlign: "center", marginBottom: 14 }} />
            {err && <p data-testid="entry-error" style={{ color: T.terraH, fontSize: 13, textAlign: "center", marginBottom: 10 }}>{err}</p>}
            <PrimaryButton testid="enter-button" onClick={enter} disabled={loading || !code.trim()}>
              {loading ? "Opening…" : "Enter"}</PrimaryButton>
            {!resendOpen ? (
              <div style={{ textAlign: "center", marginTop: 18 }}>
                <span data-testid="resend-toggle" onClick={() => { setResendOpen(true); setResendMsg(""); }}
                  style={{ ...sans, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase",
                    color: T.taupe, cursor: "pointer", fontWeight: 600 }}>
                  Didn't receive your access code?</span>
              </div>
            ) : (
              <div style={{ marginTop: 18 }} data-testid="resend-panel">
                <input data-testid="resend-email-input" type="email" value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)} placeholder="Your order email"
                  style={{ ...inputStyle, textAlign: "center", marginBottom: 10 }} />
                <PrimaryButton testid="resend-send-button" onClick={resendAccess}
                  disabled={resendLoading || !resendEmail.trim()}>
                  {resendLoading ? "Sending…" : "Send my access code"}</PrimaryButton>
                {resendMsg && <p data-testid="resend-message" style={{ ...sans, color: T.taupe,
                  fontSize: 13, textAlign: "center", lineHeight: 1.6, marginTop: 12 }}>{resendMsg}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {step === "birth" && (
        <div style={{ maxWidth: 620, margin: "0 auto", padding: "60px 22px 80px" }}>
          <h1 style={{ ...serif, fontSize: 34, color: T.brown, textAlign: "center", margin: "0 0 12px" }}>Your Birth Details</h1>
          <p style={{ ...sans, color: T.taupe, fontSize: 14.5, textAlign: "center", lineHeight: 1.6, margin: "0 0 32px" }}>
            {birthConfirm ? "Please check these details. Your chart is created once from what you enter here." : "These open the sky as it was at your birth."}</p>

          {!birthConfirm && (<>
          <div style={{ marginBottom: 24 }}>
            <div className="sh-label" style={{ marginBottom: 8 }}>Full name</div>
            <input data-testid="birth-name" style={inputStyle} placeholder="As it was first spoken"
              value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 28 }}>
            <div style={{ flex: "1 1 240px" }}>
              <div className="sh-label" style={{ marginBottom: 8 }}>Birth date</div>
              <div data-testid="birth-date" style={{ ...maskBox }}>
                <input ref={seg.day} data-testid="bd-day" inputMode="numeric" placeholder="DD" style={maskInp(2)}
                  value={dt.day} onChange={onSeg("day", 2, seg.month)} />
                <span style={maskSep}>/</span>
                <input ref={seg.month} data-testid="bd-month" inputMode="numeric" placeholder="MM" style={maskInp(2)}
                  value={dt.month} onChange={onSeg("month", 2, seg.year)} onKeyDown={onSegKey("month", seg.day)} />
                <span style={maskSep}>/</span>
                <input ref={seg.year} data-testid="bd-year" inputMode="numeric" placeholder="YYYY" style={maskInp(4)}
                  value={dt.year} onChange={onSeg("year", 4, seg.hour)} onKeyDown={onSegKey("year", seg.month)} />
              </div>
            </div>
            <div style={{ flex: "1 1 240px" }}>
              <div className="sh-label" style={{ marginBottom: 8 }}>Birth time</div>
              <div data-testid="birth-time" style={{ ...maskBox }}>
                <input ref={seg.hour} data-testid="bt-hour" inputMode="numeric" placeholder="HH" style={maskInp(2)}
                  value={dt.hour} onChange={onSeg("hour", 2, seg.minute)} />
                <span style={maskSep}>:</span>
                <input ref={seg.minute} data-testid="bt-min" inputMode="numeric" placeholder="MM" style={maskInp(2)}
                  value={dt.minute} onChange={onSeg("minute", 2, null)} onKeyDown={onSegKey("minute", seg.hour)} />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div className="sh-label" style={{ marginBottom: 8 }}>Birth time accuracy</div>
            <select data-testid="birth-accuracy" style={inputStyle} value={form.birth_time_accuracy}
              onChange={(e) => setForm({ ...form, birth_time_accuracy: e.target.value })}>
              <option value="exact">Exact — I know my birth time</option>
              <option value="approximate">Approximate — roughly this time</option>
              <option value="unknown">Unknown — I'm not sure</option>
            </select>
          </div>

          <div style={{ marginBottom: 32 }}>
            <div className="sh-label" style={{ marginBottom: 8 }}>City & country of birth</div>
            <input data-testid="birth-place" style={inputStyle} placeholder="Lisbon, Portugal"
              value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>

          {err && <p data-testid="birth-error" style={{ color: T.terraH, fontSize: 13, marginBottom: 12 }}>{err}</p>}
          <PrimaryButton testid="generate-button" onClick={reviewBirth}
            disabled={loading || !form.full_name || !form.location}>
            Review My Details</PrimaryButton>
          </>)}

          {birthConfirm && (
            <div data-testid="birth-summary">
              <div className="sh-sub" style={{ padding: "20px 0 0", borderTop: `1px solid ${T.border}` }}>
                <div className="sh-label" style={{ marginBottom: 4 }}>Full name</div>
                <p className="sh-body" style={{ margin: 0 }} data-testid="summary-name">{form.full_name}</p>
              </div>
              <div className="sh-sub" style={{ padding: "20px 0 0", borderTop: `1px solid ${T.border}` }}>
                <div className="sh-label" style={{ marginBottom: 4 }}>Birth date</div>
                <p className="sh-body" style={{ margin: 0 }} data-testid="summary-date">{`${dt.day} / ${dt.month} / ${dt.year}`}</p>
              </div>
              <div className="sh-sub" style={{ padding: "20px 0 0", borderTop: `1px solid ${T.border}` }}>
                <div className="sh-label" style={{ marginBottom: 4 }}>Birth time</div>
                <p className="sh-body" style={{ margin: 0 }} data-testid="summary-time">{`${dt.hour} : ${dt.minute}`}</p>
              </div>
              <div className="sh-sub" style={{ padding: "20px 0 0", borderTop: `1px solid ${T.border}` }}>
                <div className="sh-label" style={{ marginBottom: 4 }}>Birth time accuracy</div>
                <p className="sh-body" style={{ margin: 0 }} data-testid="summary-accuracy">{ACCURACY_LABEL[form.birth_time_accuracy]}</p>
              </div>
              <div className="sh-sub" style={{ padding: "20px 0 32px", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, marginBottom: 32 }}>
                <div className="sh-label" style={{ marginBottom: 4 }}>City & country of birth</div>
                <p className="sh-body" style={{ margin: 0 }} data-testid="summary-place">{form.location}</p>
              </div>
              {err && <p data-testid="birth-error" style={{ color: T.terraH, fontSize: 13, marginBottom: 12 }}>{err}</p>}
              <PrimaryButton testid="create-chart-button" onClick={createChart} disabled={loading}>
                {loading ? "Reading your sky…" : "Create my chart"}</PrimaryButton>
              <div style={{ textAlign: "center", marginTop: 18 }}>
                <span data-testid="edit-details-link" onClick={() => { setBirthConfirm(false); setErr(null); }}
                  style={{ ...sans, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase",
                    color: T.taupe, cursor: "pointer", fontWeight: 600 }}>
                  Go back and edit</span>
              </div>
            </div>
          )}
        </div>
      )}

      {step === "reading" && reading && <Reading reading={reading} />}
    </div>
  );
}
